/*globals showModalConfirm, globalNavigate*/
import React, { useContext, useEffect, useState } from "react";
import ActionsContext from "common/ActionsContext";

import Request from "common/Request";
import { NothingToDo, parseFields, getRowsForTable } from "./api";
import View from "views/Config/Profiles/Common/View";
import Table from "common/graphs/Table/Table";
import Modal from "common/Modal";
import { NumberCell } from "common/graphs/Table/TableRenders";
import { getIndexes } from "common/utils";
import styled from "styled-components";

const TableContainer = styled.div`
  table.MuiTable-root {
    min-width: auto !important;
    max-width: auto !important;
  }
`;

const ExpandButtonsWrapper = styled.div`
  display: inline-block;
`;

const FooterMessage = styled.div`
  color: red;
  ul {
    padding: 0 0 0 15px;
    margin: 0px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
      text-align: left;
    }
  }
`;

const doesNothing = () => {};

const useProfiles = (type = "unknown", load = undefined) => {
  const [request, setRequest] = useState(null);
  const doLoad = () => {
    setRequest(null);
    setRequest(
      load === undefined
        ? ifCl.run(`show profile ${type}`).then((response) => ({ response }))
        : load()
    );
  };
  return [request, doLoad];
};

const CreateProfileButton = ({ onClick }) => (
  <button
    type="button"
    className="btn btn-default waves-effect shadow-none"
    onClick={onClick}
  >
    <i className="material-icons">add_circle</i>
    <span>Add Profile...</span>
  </button>
);

const CANT_PROCEED_ERROR =
  "Cannot proceed while there are pending config changes." +
  " Try again later.";

const clearConfigAttempt = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

const checkNothingPending = () =>
  ifCl.run("show config diff").then((response) => {
    if (response.length > 0) {
      throw new Error(CANT_PROCEED_ERROR);
    }
  });

const notifyAndRevert = (error) => {
  error = error.message === undefined ? new Error(error) : error;
  clearConfigAttempt();
  throw error;
};

class UserDeclined extends Error {}

const nestedEntries = (fields = []) =>
  fields
    .filter(({ type }) => type === "many")
    .map(({ label }) => label)
    .join(", ") || "profile data";

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  fields = [],
  onApply,
  rules,
}) => {
  if (!showDeleteModal) {
    return null;
  }
  const { delete: itemsToDelete, notDelete } = items.reduce(
    (acc, item) => {
      const isInRule = rules.filter((rule) =>  rule.includes(` ${item.name} `));
      if (isInRule.length > 0) {
        acc.notDelete.push(item);
      } else {
        acc.delete.push(item);
      }
      return acc;
    },
    { delete: [], notDelete: [] },
  );

  const names = itemsToDelete.map((item) => item.name);
  const title =
    itemsToDelete.length === 1
      ? `Delete "${itemsToDelete[0].name}" profile?`
      : `Delete ${itemsToDelete.length} profiles?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={names.length > 1 && names.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {itemsToDelete.length === 1 ? (
            <p>
              This will delete this profile and all the {nestedEntries(fields)}{" "}
              entries in this profile.
            </p>
          ) : null}
          {itemsToDelete.length > 1 ? (
            <>
              {itemsToDelete.length < 100 ? (
                <ul>
                  {itemsToDelete.map((item) => (
                    <li key={item.name}>{item.name}</li>
                  ))}
                </ul>
              ) : null}
              <p>
                This will delete these profiles and all the{" "}
                {nestedEntries(fields)} entries in these profiles.
              </p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={() => {
        setShowDeleteModal(false);
        onApply(itemsToDelete);
      }}
      footerMessage={
        notDelete.length > 0 ? (
          <FooterMessage>
            <p>These profiles cannot be deleted, because they are part of a rule.</p>
            <ul>
              {notDelete.map((item, i) => (
                <li key={i}>{item.name}</li>
              ))}
            </ul>
          </FooterMessage>
        ) :  null
      }
    />
  );
};

const ignoreNormalOutcomes = (error) => {
  if (error instanceof UserDeclined || error instanceof NothingToDo) {
    console.log("Process cancelled");
  } else {
    throw error;
  }
};

const submitCommand = (command) => ifCl.run(`${command}\n`, /*is batch*/ true);

const UpdateDpiProfilesButton = () => {
  const actions = useContext(ActionsContext);
  return (
    <button
      type="button"
      className="btn btn-default waves-effect shadow-none"
      onClick={() => actions.send("update-predfined-profiles")}
    >
      <i className="material-icons">swap_vert</i>
      <span>Update predefined profiles...</span>
    </button>
  );
};

export const Profiles = ({
  cliType = "unknown",
  typeLabel = "Unknown",
  viewURL = "Uknown",
  fields = [],
  composeApplyCommand = doesNothing,
  edit = null,
  create = null,
  load = undefined,
  returnView,
  profileFn = undefined,
  rules = [],
}) => {
  const [error, setError] = useState(null);
  const [request, doLoad] = useProfiles(cliType, load);
  const actions = useContext(ActionsContext);
  const [collapsed, setCollapsed] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [selected, setSelected] = useState([]);

  const clearError = () => setError(null);
  const clearAndDoLoad = () => clearError() || doLoad();

  const apply = (settings, originalSettings = []) => {
    clearError();
    const buildCommand = () => composeApplyCommand(originalSettings, settings);

    return checkNothingPending()
      .then(buildCommand)
      .then(submitCommand)
      .catch(ignoreNormalOutcomes)
      .catch(notifyAndRevert)
      .catch(setError);
  };

  const requestDeletion = (profiles) =>
    apply(profiles.map((profile) => ({ ...profile, deleted: true, stored: true })));

  useEffect(() => {
    if (edit === null && create === null) {
      clearAndDoLoad();
    }
    return actions.recv("do-load", clearAndDoLoad);
  }, [edit, create]);

  const doEditProfile = ({ name }) => globalNavigate(viewURL, { edit: name });

  const doCreateProfile = () => globalNavigate(viewURL, { create: true });

  const doDeleteProfiles = (profiles) => {
    checkNothingPending()
      .then(()=>requestDeletion(profiles))
      .then(() => {
        setSelected([]);
        doLoad();
      });
  };

  const { columns, manyAttr, fieldsSub: fieldsSub } = parseFields(fields);

  const actionsColumn = {
    label: "ACTIONS",
    id: "ACTIONS",
    width: "15%",
    colClassName: "text-center",
    actions: [
      {
        label: "Edit",
        icon: "edit",
        onClick: (row) => {
          doEditProfile({ name: row["NAME"] });
        },
      },
      {
        label: "Delete",
        icon: "delete",
        onClick: (row) => {
          const profile = profileFn(row);
          setDeleteSelected([profile]);
          setShowDeleteModal(true);
        },
      },
    ],
  };

  const columnsTotal = [...columns, actionsColumn];

  return request === null ? null : (
    <View
      title={`${typeLabel} profiles`}
      applyActionClassName="hidden-to-operators"
      returnView={returnView}
      error={error}
      setError={setError}
      createAction={doCreateProfile}
    >
      <div className="margin-b-5">
        <CreateProfileButton onClick={doCreateProfile} />
        {typeLabel === "DPI" ? <UpdateDpiProfilesButton /> : null}
        {manyAttr && fieldsSub ? (
          <ExpandButtonsWrapper>
            <button
              type="button"
              className="btn btn-default waves-effect shadow-none"
              onClick={() => setCollapsed(false)}
            >
              <i className="material-icons">expand_more</i>
              <span>Expand All</span>
            </button>
            <button
              className="btn btn-default waves-effect shadow-none"
              type="button"
              onClick={() => setCollapsed(true)}
            >
              <i className="material-icons">expand_less</i>
              <span>Collapse All</span>
            </button>
          </ExpandButtonsWrapper>
        ) : null}
      </div>
      <Request during={request}>
        {({ response }) => {
          const tableData = getRowsForTable(response, manyAttr, fieldsSub).map((row, index) => ({
            ...row,
            id: row.id !== undefined ? row.id : index
          }));

          return (
            <TableContainer>
              <Table
                columns={columnsTotal}
                isSelectable={true}
                isRowSelectable={false}
                data={tableData}
                exportCSV={() => response}
                csvPrefix={`${typeLabel}-profiles`}
                rowsPerPageNr={50}
                collapsed={collapsed}
                selected={selected}
                setSelected={setSelected}
                deleteSelected={(values) => {
                  const selectedItems = tableData
                    .filter((row) => values.includes(row.id))
                    .map((row) => profileFn(row));
                  setDeleteSelected(selectedItems);
                  setShowDeleteModal(true);
                }}
              />
            </TableContainer>
          );
        }}
      </Request>
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        items={deleteSelected}
        fields={fields}
        onApply={doDeleteProfiles}
        rules={rules}
      />
    </View>
  );
};
